import { useSession } from './session';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

// Thrown when the server could not be reached at all, as opposed to answering with an error.
export class OfflineError extends Error {}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    throw new OfflineError('Der Server ist nicht erreichbar.');
  }
  // Any 401 means the session is gone, whichever screen asked. A failed login is not a lost
  // session. A run in progress stays on screen (see sessionGate.ts).
  if (response.status === 401 && path !== '/api/auth/login') {
    useSession.setState({ user: null, state: 'anonymous' });
  }
  if (!response.ok) {
    const message = await response
      .json()
      .then((data: { message?: string }) => data.message)
      .catch(() => undefined);
    throw new ApiError(response.status, message ?? 'Der Server hat einen Fehler gemeldet.');
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
